import {
	CSSProperties,
	KeyboardEvent as ReactKeyboardEvent,
	MouseEvent as ReactMouseEvent,
	ReactElement,
	ReactNode,
	Ref,
	cloneElement,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
	useState
} from 'react'
import { FloatingPortal } from '@/components/FloatingPortal'
import { useFloatingPosition } from '@/hooks/useFloatingPosition'
import { useFocusScope } from '@/hooks/useFocusScope'
import { useDismissLayer } from '@/hooks/useDismissLayer'

type PopoverPlacement = 'top' | 'bottom' | 'left' | 'right'
type PopoverAlign = 'start' | 'center' | 'end'
type PopoverRole = 'dialog' | 'listbox'

interface PopoverTriggerProps {
	ref?: Ref<HTMLElement>
	onClick?: (e: ReactMouseEvent<HTMLElement>) => void
	'aria-haspopup'?: PopoverRole
	'aria-expanded'?: boolean
	'aria-controls'?: string
}

interface PopoverProps {
	/** A single focusable element (usually a `<button>`); ARIA and a ref are injected into it. */
	trigger: ReactElement<PopoverTriggerProps>
	/** Rendered beside the trigger inside the anchor, for controls that must not nest in it. */
	triggerAdornment?: ReactNode
	children: ReactNode
	role?: PopoverRole
	/** Accessible name used when no `usePopoverTitleId` heading is rendered in the panel. */
	ariaLabel?: string
	/** Keep Tab inside the panel. Disable for listbox-style popups, where Tab closes and moves on. */
	trapFocus?: boolean
	placement?: PopoverPlacement
	align?: PopoverAlign
	open?: boolean
	onOpenChange?: (open: boolean) => void
	radius?: number
	padding?: number
	gap?: number
	minWidth?: number
	maxWidth?: number
	maxHeight?: number | null
	matchTriggerWidth?: boolean
	spacesClass?: string
	className?: string
	containerClassName?: string
}

const PopoverTitleContext = createContext<((id: string | undefined) => void) | null>(null)

/** Returns an id for the panel's heading and registers it as the panel's `aria-labelledby`. */
export function usePopoverTitleId (): string {
	const registerTitle = useContext(PopoverTitleContext)
	const id = useId()

	useLayoutEffect(() => {
		if (!registerTitle) return
		registerTitle(id)
		return () => { registerTitle(undefined) }
	}, [registerTitle, id])

	return id
}

function assignRef<T> (ref: Ref<T> | undefined, value: T | null) {
	if (typeof ref === 'function') ref(value)
	else if (ref) ref.current = value
}

export function Popover ({
	trigger,
	triggerAdornment,
	children,
	role = 'dialog',
	ariaLabel,
	trapFocus = true,
	placement = 'bottom',
	align = 'start',
	open: controlledOpen,
	onOpenChange,
	radius = 12,
	padding = 4,
	gap = 0,
	minWidth = 200,
	maxWidth,
	maxHeight = 280,
	matchTriggerWidth = false,
	spacesClass = '',
	className = '',
	containerClassName = ''
}: PopoverProps) {
	const [internalOpen, setInternalOpen] = useState(false)
	const isControlled = controlledOpen !== undefined
	const isOpen = isControlled ? controlledOpen : internalOpen
	const containerRef = useRef<HTMLDivElement>(null)
	const panelRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLElement | null>(null)
	const openedByPointerRef = useRef(false)
	const returnFocusRef = useRef(true)
	const panelId = useId()
	const [titleId, setTitleId] = useState<string>()

	const { style, anchorWidth, availableHeight } = useFloatingPosition(
		containerRef,
		panelRef,
		{ open: isOpen, placement, align, offset: 8 }
	)

	const setOpen = useCallback(
		(val: boolean) => {
			if (!isControlled) setInternalOpen(val)
			onOpenChange?.(val)
		},
		[isControlled, onOpenChange]
	)

	const close = useCallback(
		(returnFocus: boolean) => {
			returnFocusRef.current = returnFocus
			setOpen(false)
		},
		[setOpen]
	)

	useDismissLayer(isOpen, () => { close(true) })

	useFocusScope(panelRef, isOpen, {
		trap: trapFocus,
		// A pointer-opened dialog focuses the panel itself, so no ring flashes on its first field.
		initialFocus: () =>
			role === 'dialog' && openedByPointerRef.current ? panelRef.current : null,
		returnFocus: () => (returnFocusRef.current ? triggerRef.current : null)
	})

	useEffect(() => {
		if (!isOpen) return
		returnFocusRef.current = true

		// The panel is portalled out of the container, so test it separately.
		const isInside = (target: EventTarget | null) =>
			target instanceof Node &&
			(!!containerRef.current?.contains(target) ||
				!!panelRef.current?.contains(target))

		// Dismissed by interacting elsewhere: leave focus where the user put it.
		const handleOutside = (e: Event) => {
			if (!isInside(e.target)) close(false)
		}

		document.addEventListener('mousedown', handleOutside)
		document.addEventListener('touchstart', handleOutside, { passive: true })
		document.addEventListener('focusin', handleOutside)
		return () => {
			document.removeEventListener('mousedown', handleOutside)
			document.removeEventListener('touchstart', handleOutside)
			document.removeEventListener('focusin', handleOutside)
		}
	}, [isOpen, close])

	const handlePanelKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
		if (trapFocus || e.key !== 'Tab') return
		// Tab continues from the trigger, since the portalled panel sits at the end of <body>.
		if (e.shiftKey) e.preventDefault()
		triggerRef.current?.focus()
		close(false)
	}

	const triggerProps = trigger.props

	const setTriggerRef = useCallback(
		(node: HTMLElement | null) => {
			triggerRef.current = node
			assignRef(triggerProps.ref, node)
		},
		[triggerProps.ref]
	)

	const triggerElement = cloneElement(trigger, {
		ref: setTriggerRef,
		onClick: (e: ReactMouseEvent<HTMLElement>) => {
			triggerProps.onClick?.(e)
			if (e.defaultPrevented) return
			// Keyboard-activated clicks report `detail === 0`.
			openedByPointerRef.current = e.detail > 0
			setOpen(!isOpen)
		},
		'aria-haspopup': role,
		'aria-expanded': isOpen,
		'aria-controls': isOpen ? panelId : undefined
	})

	const radiusMap: Record<number, string> = {
		0: '', 4: 'spaces:rounded-4', 8: 'spaces:rounded-8', 12: 'spaces:rounded-12', 16: 'spaces:rounded-16', 24: 'spaces:rounded-24'
	}
	const paddingMap: Record<number, string> = {
		0: '', 4: 'spaces:p-4', 8: 'spaces:p-8', 12: 'spaces:p-12', 16: 'spaces:p-16', 24: 'spaces:p-24'
	}
	const gapMap: Record<number, string> = {
		0: '', 4: 'spaces:flex spaces:flex-col spaces:gap-4', 8: 'spaces:flex spaces:flex-col spaces:gap-8', 12: 'spaces:flex spaces:flex-col spaces:gap-12', 16: 'spaces:flex spaces:flex-col spaces:gap-16'
	}

	const panelClasses = [
		'spaces-popover spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:shadow-sm-below',
		radiusMap[radius] ?? '',
		paddingMap[padding] ?? '',
		gapMap[gap] ?? '',
		spacesClass,
		'spaces:overflow-hidden spaces:overflow-y-auto'
	]
		.filter(Boolean)
		.join(' ')

	// Never taller than the space left on the resolved side; scroll instead.
	const effectiveMaxHeight =
		maxHeight != null ? Math.min(maxHeight, availableHeight) : availableHeight

	const panelStyle: CSSProperties = {
		...style,
		...(matchTriggerWidth
			? { minWidth: anchorWidth }
			: minWidth != null && { minWidth: `${minWidth}px` }),
		...(maxWidth != null && { maxWidth: `${maxWidth}px` }),
		maxHeight: `${effectiveMaxHeight}px`
	}

	return (
		<div
			ref={containerRef}
			className={`spaces:relative spaces:inline-flex ${containerClassName}`}
		>
			{triggerElement}
			{triggerAdornment}
			{isOpen && (
				<FloatingPortal>
					<div
						ref={panelRef}
						id={panelId}
						role={role}
						aria-modal={role === 'dialog' && trapFocus ? true : undefined}
						aria-labelledby={titleId}
						aria-label={titleId ? undefined : ariaLabel}
						tabIndex={-1}
						onKeyDown={handlePanelKeyDown}
						className={`spaces:fixed spaces:z-[2147483100] spaces:outline-none ${panelClasses} ${className}`}
						style={panelStyle}
					>
						<PopoverTitleContext.Provider value={setTitleId}>
							{children}
						</PopoverTitleContext.Provider>
					</div>
				</FloatingPortal>
			)}
		</div>
	)
}
